// Library imports
import React, { useState } from "react";
import { useDispatch } from "react-redux";

// User Component imports
import { PageContent, PageHeader } from 'components/common/layout_element/LayoutElement';
import { Row, Stack, Text, TextInput, Button, Modal, LoadingBox } from "_core/components/common/UI";
import * as Constants from "_core/configs/Constants";
import { setLoading, setErrorModal } from "_core/slices/scene/SceneSlice";
import AdminOpsConfigGetApi from "apis/AdminOpsConfigGetApi";
import AdminOpsConfigUpdateApi from "apis/AdminOpsConfigUpdateApi";
import { useOperatorPage } from "./common/useOperatorPage";
import OpsDomainListField, { useDomainRows } from "./common/OpsDomainListField";
import {
    OPS_CONFIG_FIELDS, configValue, fieldError, usageWarning, buildConfigChanges,
    configErrorCount, formatConfigValue, configUpdateParams,
} from "./utils/opsConfig";
import { hasDomainErrors, joinDomains, domainsChanged, domainDiff } from "./utils/opsDomain";

import './OpsCommon.scss';
import './AdminOpsConfig.scss';

const AdminOpsConfig = () => {
    const dispatch = useDispatch();
    const ExecGet = AdminOpsConfigGetApi();
    const ExecUpdate = AdminOpsConfigUpdateApi();

    const [saved, setSaved] = useState(null);
    const [defaults, setDefaults] = useState({});
    const [usage, setUsage] = useState({});
    const [draft, setDraft] = useState({});
    // 読込・保存のたびに入力欄を作り直し、表示を保存済みの値に揃える
    const [formVersion, setFormVersion] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [savedDomains, setSavedDomains] = useState([]);
    const domainRows = useDomainRows();

    const showError = (response) => {
        dispatch(setErrorModal({ show: true, text: response?.error_detail ?? { message: Constants.UNKNOWN_ERROR_DETAIL } }));
    };

    const applySaved = (config, domains) => {
        setSaved(config);
        setDraft({});
        setSavedDomains(domains);
        domainRows.reset(domains);
        setFormVersion((v) => v + 1);
    };

    const Load = async () => {
        const { response } = await ExecGet();
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        setDefaults(response.defaults);
        setUsage({ talk_count: response.talk_count, evaluation_count: response.evaluation_count });
        applySaved(response.config, response.domains);
    };

    const { canUse } = useOperatorPage(Load);

    const changes = buildConfigChanges(saved, draft);
    const draftDomains = domainRows.values;
    const domainChanged = domainsChanged(savedDomains, draftDomains);
    const diff = domainDiff(savedDomains, draftDomains);
    // 既存の不正な値は表示だけして、許可ドメインを触らない限り他の設定の保存は止めない
    const canSave = !!saved && configErrorCount(saved, draft) === 0
        && (changes.length > 0 || domainChanged)
        && !(domainChanged && hasDomainErrors(draftDomains));

    const setValue = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        setIsConfirmOpen(false);
        if (!canSave) return;
        dispatch(setLoading(true));
        const params = configUpdateParams(changes);
        if (domainChanged) params.domain = joinDomains(draftDomains);
        const { response } = await ExecUpdate(params);
        dispatch(setLoading(false));
        if (!response || response.error_code !== 0) {
            showError(response);
            return;
        }
        applySaved(response.config, response.domains);
    };

    const renderIntField = (field, value) => {
        const error = fieldError(field, value);
        const warning = field.usageKey ? usageWarning(field, value, usage[field.usageKey]) : "";
        return (
            <>
                <div className="ops-config-num-row">
                    <TextInput type="number" parentClassName={"ops-config-num-input" + (error ? " is-error" : "")}
                        default={String(value)} onchange={(e) => setValue(field.key, e.target.value)} />
                    <span className="ops-config-unit">{field.unit}</span>
                </div>
                {field.usageKey &&
                    <div className="ops-setting-sub">現在の利用回数 {Number(usage[field.usageKey] ?? 0).toLocaleString("ja-JP")} 回</div>
                }
                {error && <div className="ops-setting-error">{error}</div>}
                {warning && <div className="ops-setting-error">{warning}</div>}
            </>
        );
    };

    const renderBoolField = (field, value) => {
        const on = value === 1;
        return (
            <div className="ops-config-bool-row">
                <label className={"ops-config-switch " + (on ? "on" : "off")}>
                    <input type="checkbox" checked={on} onChange={(e) => setValue(field.key, e.target.checked ? 1 : 0)} />
                    <span className="switch-track"><span className="switch-thumb" /></span>
                </label>
                <div className={"ops-config-state " + (on ? "on" : "off")}>{on ? "ON" : "OFF"}</div>
            </div>
        );
    };

    const confirmBody = (
        <Stack className="ops-config-confirm spacing-12px padding-16px">
            <Text>次の設定を変更します。保存後すぐに対話画面へ反映されます。</Text>
            <div className="ops-config-changes">
                {changes.map(({ field, before, after }) => (
                    <div key={field.key} className="ops-config-change">
                        <div className="ops-config-change-name">
                            <div className="ops-config-change-label">{field.label}</div>
                            <div className="ops-config-change-key">{field.key}</div>
                        </div>
                        <div className="ops-config-change-values">
                            <span className="ops-config-change-before">{formatConfigValue(field, before)}</span>
                            <i className="bi bi-arrow-right" />
                            <span className="ops-config-change-after">{formatConfigValue(field, after)}</span>
                        </div>
                    </div>
                ))}
                {domainChanged &&
                    <div className="ops-config-change">
                        <div className="ops-config-change-name">
                            <div className="ops-config-change-label">許可ドメイン</div>
                            <div className="ops-config-change-key">domain</div>
                        </div>
                        <div className="ops-config-change-domains">
                            {diff.added.map((d) => <span key={"+" + d} className="ops-config-change-after">+ {d}</span>)}
                            {diff.removed.map((d) => <span key={"-" + d} className="ops-config-change-before">− {d}</span>)}
                            {diff.added.length === 0 && diff.removed.length === 0 &&
                                <span className="ops-config-change-after">表記・並び順を変更</span>
                            }
                        </div>
                    </div>
                }
            </div>
        </Stack>
    );

    return (
        <PageContent pageId="AdminOpsConfig">
            <div className="ops-scope width-full">
                <PageHeader>
                    <Row className="flex-both align-center width-full">
                        <Stack className="spacing-4px">
                            <div className="page-header-title">利用設定</div>
                            <div className="page-header-detail">現在ログイン中のアカウントの許可ドメイン・利用上限と、検索・応答生成の動作を変更します。</div>
                        </Stack>
                        <Button className={"ops-config-update-button width-240px " + (canSave ? "primary" : "primary disabled")}
                            disabled={!canSave} onClick={() => setIsConfirmOpen(true)}>
                            更新
                        </Button>
                    </Row>
                </PageHeader>

                <div className="ops-config-body">
                    {!saved ? <div className="ops-config-loading">{canUse && <LoadingBox />}</div> :
                        <div className="ops-setting-card" key={formVersion}>
                            <div className="ops-setting-row">
                                <div className="ops-setting-label">
                                    <Text className="ops-setting-label-text">許可ドメイン</Text>
                                    <div className="ops-setting-label-hint">{"ウィジェット・対話画面を設置できるドメインです。\n*.example.com でサブドメインをまとめて許可できます。"}</div>
                                </div>
                                <div className="ops-setting-field">
                                    <OpsDomainListField domainRows={domainRows} />
                                </div>
                            </div>
                            {OPS_CONFIG_FIELDS.map((field) => {
                                const value = configValue(saved, draft, field.key);
                                return (
                                    <React.Fragment key={field.key}>
                                        <hr />
                                        <div className="ops-setting-row">
                                            <div className="ops-setting-label">
                                                <Text className="ops-setting-label-text">{field.label}</Text>
                                                <div className="ops-setting-label-hint">{field.desc}</div>
                                            </div>
                                            <div className="ops-setting-field">
                                                {field.type === "int" ? renderIntField(field, value) : renderBoolField(field, value)}
                                                {field.key in defaults &&
                                                    <div className="ops-setting-sub">既定値: {formatConfigValue(field, defaults[field.key])}</div>
                                                }
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    }
                </div>

                <Modal
                    className={isConfirmOpen ? "show" : ""}
                    dialogclass="modal-dialog-centered"
                    name="ops-config-confirm-modal"
                    title="利用設定を更新"

                    showClose={false}

                    showCancel={true}
                    cancel={() => setIsConfirmOpen(false)}
                    cancelbtnclass="secondary-border"
                    cancelvalue="キャンセル"

                    showConfirm={true}
                    confirm={save}
                    confirmbtnclass="primary"
                    confirmvalue="更新"

                    body={confirmBody}
                />
            </div>
        </PageContent>
    );
};

export default AdminOpsConfig;
